import React from "react";
import PropTypes from "prop-types";
import { Cell, Legend, Pie, PieChart, Tooltip } from "recharts";
import Chart from "./Chart";
import ChartTooltip from "./ChartTooltip";
import { CHART_THEME, getChartColor } from "./chartTheme";

// Single component that renders either a solid pie chart or a donut chart
// depending on the `variant` prop. Keeping them in one place avoids
// duplicating the slice / legend / tooltip wiring.
const AppPieChart = ({
  title,
  subtitle,
  action,
  data = [],
  nameKey = "name",
  valueKey = "value",
  variant = "pie",
  height = 280,
  innerRadius,
  outerRadius = "80%",
  tooltipFormatter,
  emptyMessage,
  showLegend = true,
}) => {
  const isEmpty =
    !data?.length || data.every((entry) => !entry?.[valueKey]);

  const resolveInnerRadius = () => {
    if (innerRadius !== undefined) return innerRadius;
    return variant === "donut" ? "55%" : 0;
  };
  const resolvedInnerRadius = resolveInnerRadius();

  return (
    <Chart
      title={title}
      subtitle={subtitle}
      action={action}
      height={height}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    >
      <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
        <Tooltip content={<ChartTooltip valueFormatter={tooltipFormatter} />} />
        {showLegend && (
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{ fontSize: 12, color: CHART_THEME.axisColor }}
          />
        )}
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          innerRadius={resolvedInnerRadius}
          outerRadius={outerRadius}
          paddingAngle={variant === "donut" ? 2 : 0}
          stroke="#ffffff"
          strokeWidth={2}
        >
          {data.map((entry, index) => (
            <Cell
              key={`${entry[nameKey]}-${index}`}
              fill={entry.color || getChartColor(index)}
            />
          ))}
        </Pie>
      </PieChart>
    </Chart>
  );
};

AppPieChart.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  action: PropTypes.node,
  data: PropTypes.array,
  nameKey: PropTypes.string,
  valueKey: PropTypes.string,
  variant: PropTypes.oneOf(["pie", "donut"]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  innerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  outerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tooltipFormatter: PropTypes.func,
  emptyMessage: PropTypes.string,
  showLegend: PropTypes.bool,
};

export default AppPieChart;
